import React, { useState, useRef, useEffect } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const DAYS_OF_WEEK = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(date, start, end) {
  if (!start || !end || !date) return false;
  const t = date.getTime();
  return t > start.getTime() && t < end.getTime();
}

function formatDisplay(date) {
  if (!date) return 'MM/DD/YYYY';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function toISODate(date) {
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function CalendarPanel({ value, onChange, onCancel, otherDate, isStart }) {
  const now = new Date();
  const initial = value || now;
  const [view, setView] = useState('calendar');
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [tempDate, setTempDate] = useState(value);

  useEffect(() => {
    setTempDate(value);
    if (value) {
      setViewYear(value.getFullYear());
      setViewMonth(value.getMonth());
    }
  }, [value]);

  const handleMonthSelect = (monthIdx) => {
    if (isMonthDisabled(monthIdx)) return;
    setViewMonth(monthIdx);
    setView('calendar');
  };

  // Bound the selectable range so an end date can never precede its start date
  // (and a start date can never follow its end date). Same day is allowed.
  const minDate = isStart ? null : otherDate;
  const maxDate = isStart ? otherDate : null;

  const isDayDisabled = (date) =>
    (minDate && date.getTime() < minDate.getTime()) ||
    (maxDate && date.getTime() > maxDate.getTime());

  // A month is disabled only when ALL of its days are out of range, so a month
  // that merely contains the start/end date stays selectable (consistent with
  // the day view, which disables individual out-of-range days within it).
  const isMonthDisabled = (monthIdx) => {
    const monthStart = new Date(viewYear, monthIdx, 1);
    const monthEnd = new Date(viewYear, monthIdx + 1, 0);
    if (minDate && monthEnd.getTime() < minDate.getTime()) return true;
    if (maxDate && monthStart.getTime() > maxDate.getTime()) return true;
    return false;
  };

  const handleDayClick = (day) => {
    const clicked = new Date(viewYear, viewMonth, day);
    if (isDayDisabled(clicked)) return;
    setTempDate(clicked);
  };

  const handleConfirm = () => {
    if (tempDate) onChange(tempDate);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const rangeStart = isStart ? tempDate : otherDate;
  const rangeEnd = isStart ? otherDate : tempDate;

  return (
    <div className="w-full">
      {view === 'months' ? (
        <>
          <div className="flex items-center justify-between px-2 mb-3">
            <button type="button" onClick={() => setViewYear(y => y - 1)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <span className="text-sm font-semibold text-gray-800">{viewYear}</span>
            <button type="button" onClick={() => setViewYear(y => y + 1)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 px-1">
            {MONTHS.map((m, i) => {
              const isSelected = tempDate && tempDate.getFullYear() === viewYear && tempDate.getMonth() === i;
              const isDisabled = isMonthDisabled(i);
              return (
                <button
                  type="button"
                  key={m}
                  onClick={() => handleMonthSelect(i)}
                  disabled={isDisabled}
                  className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-[#2492B6] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between px-1 mb-2">
            <button type="button" onClick={() => {
              if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
              else setViewMonth(m => m - 1);
            }} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <button
              type="button"
              onClick={() => setView('months')}
              className="text-sm font-semibold text-gray-800 hover:text-[#2492B6] transition-colors flex items-center gap-1"
            >
              {MONTHS[viewMonth]} {viewYear}
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </button>
            <button type="button" onClick={() => {
              if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
              else setViewMonth(m => m + 1);
            }} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0 mb-1 max-w-[252px] mx-auto">
            {DAYS_OF_WEEK.map((d, i) => (
              <div key={i} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0 max-w-[252px] mx-auto">
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="h-9" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(viewYear, viewMonth, day);
              const isSelectedDay = isSameDay(date, tempDate);
              const isOther = isSameDay(date, otherDate);
              const inRange = rangeStart && rangeEnd && isInRange(date, rangeStart, rangeEnd);

              const isSelected = isSelectedDay || isOther;
              const isRange = !isSelected && inRange;
              const isDisabled = isDayDisabled(date);

              const dayStyle = {
                borderRadius: isRange ? '0px' : '4px',
                ...(isSelected && !isDisabled && { boxShadow: '0 4px 10px rgba(36,146,182,0.3)' }),
              };

              return (
                <button
                  type="button"
                  key={day}
                  onClick={() => handleDayClick(day)}
                  disabled={isDisabled}
                  style={dayStyle}
                  className={`h-9 w-9 flex items-center justify-center text-sm font-medium transition-all ${
                    isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-[#2492B6] text-white'
                        : isRange
                          ? 'bg-[#2492B6]/10 text-[#2492B6]'
                          : 'text-gray-800 hover:bg-[#2492B6]/20'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Cancel / Confirm */}
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!tempDate}
          className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${
            tempDate
              ? 'bg-[#2492B6] text-white hover:bg-[#1d7a96]'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

function DateRangePicker({ startDate, endDate, onStartChange, onEndChange, hideLabel = false }) {
  const [activePanel, setActivePanel] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setActivePanel(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const startDateObj = startDate ? new Date(startDate + 'T00:00:00') : null;
  const endDateObj = endDate ? new Date(endDate + 'T00:00:00') : null;

  return (
    <div className="relative" ref={containerRef}>
      {!hideLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date range</label>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setActivePanel(activePanel === 'start' ? null : 'start')}
          className={`flex-1 flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm text-left transition-colors ${
            activePanel === 'start'
              ? 'border-[#2492B6] ring-2 ring-[#2492B6]/20'
              : 'border-gray-300'
          }`}
        >
          <span className={startDateObj ? 'text-gray-700' : 'text-gray-400'}>{formatDisplay(startDateObj)}</span>
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        </button>
        <span className="text-gray-400 text-sm flex-shrink-0">—</span>
        <button
          type="button"
          onClick={() => setActivePanel(activePanel === 'end' ? null : 'end')}
          className={`flex-1 flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm text-left transition-colors ${
            activePanel === 'end'
              ? 'border-[#2492B6] ring-2 ring-[#2492B6]/20'
              : 'border-gray-300'
          }`}
        >
          <span className={endDateObj ? 'text-gray-700' : 'text-gray-400'}>{formatDisplay(endDateObj)}</span>
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        </button>
      </div>

      {activePanel && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-2xl border border-gray-200 shadow-xl p-4 z-20">
          <CalendarPanel
            value={activePanel === 'start' ? startDateObj : endDateObj}
            otherDate={activePanel === 'start' ? endDateObj : startDateObj}
            isStart={activePanel === 'start'}
            onCancel={() => setActivePanel(null)}
            onChange={(date) => {
              const iso = toISODate(date);
              if (activePanel === 'start') {
                onStartChange(iso);
                setActivePanel('end');
              } else {
                onEndChange(iso);
                setActivePanel(null);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
